import React, { useEffect, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { API_ENDPOINTS } from '../config/apiConfig';

const STORAGE_KEY = 'lmsAcademicYear';

export interface AcademicYears {
  years: number[];
  current: number | null;
  previous: number | null;
}

/** Reads the stored year, ignoring anything that is not a number. */
const readStoredYear = (): number | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

interface AcademicYearToggleProps {
  /** Called with the year to scope to, once known and whenever it changes. */
  onChange: (year: number | null) => void;
}

/**
 * Switches the LMS between academic years, matching the UMS control.
 *
 * The years come from the tests themselves rather than a fixed list, so a season
 * appears here as soon as a test is tagged with it.
 */
const AcademicYearToggle: React.FC<AcademicYearToggleProps> = ({ onChange }) => {
  const [years, setYears] = useState<AcademicYears>({ years: [], current: null, previous: null });
  const [selected, setSelected] = useState<number | null>(readStoredYear());

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    fetch(`${API_ENDPOINTS.learningApi}/packages/academic-years`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((response) => response.json())
      .then((body) => {
        if (cancelled) return;
        const data: AcademicYears = body?.data ?? { years: [], current: null, previous: null };
        setYears(data);
        // Fall back to current when nothing is stored, or the stored year has gone.
        setSelected((previous) =>
          previous != null && data.years.includes(previous) ? previous : data.current
        );
      })
      .catch(() => {
        // Without the list the toggle stays hidden; the LMS shows every year.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (selected != null) {
      localStorage.setItem(STORAGE_KEY, String(selected));
    }
    onChange(selected);
  }, [selected, onChange]);

  if (years.current == null) {
    return null;
  }

  const buttonSx = {
    color: 'rgba(255, 255, 255, 0.75)',
    borderColor: 'rgba(255, 255, 255, 0.4)',
    px: 1.5,
    py: 0.25,
    textTransform: 'none' as const,
    '&.Mui-selected': {
      color: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.22)',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.35)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: { xs: 'none', sm: 'block' } }}>
        Academic Year
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={selected ?? years.current}
        // ToggleButtonGroup emits null when the active button is clicked again.
        onChange={(_event, value) => value != null && setSelected(value)}
        aria-label="academic year"
      >
        <Tooltip title={years.previous == null ? 'No earlier academic year on record' : `Show ${years.previous}`}>
          <span>
            <ToggleButton value={years.previous ?? -1} disabled={years.previous == null} sx={buttonSx}>
              Previous{years.previous != null ? ` · ${years.previous}` : ''}
            </ToggleButton>
          </span>
        </Tooltip>
        <ToggleButton value={years.current} sx={buttonSx}>
          Current · {years.current}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default AcademicYearToggle;
